<template>
  <div
    ref="rootRef"
    class="ui-select ui-multi-select"
    :class="attrs.class"
    :style="attrs.style as StyleValue"
  >
    <button
      v-bind="buttonAttrs"
      :id="triggerId"
      ref="triggerRef"
      type="button"
      class="ui-select__trigger"
      role="combobox"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :aria-activedescendant="isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined"
      :disabled="disabled"
      @click="onTriggerClick"
      @keydown="onKeydown"
    >
      <span
        ref="valueRef"
        class="ui-select__value"
        :class="{ 'is-placeholder': selectedOptions.length === 0 }"
      >
        {{ summary }}
      </span>

      <ChevronDown class="ui-select__chevron" :size="18" :stroke-width="2.4" aria-hidden="true" />
    </button>

    <Teleport to="body">
      <Transition name="ui-pop">
        <ul
          v-if="isOpen"
          :id="listboxId"
          ref="panelRef"
          class="ui-select__list"
          role="listbox"
          aria-multiselectable="true"
          :aria-labelledby="triggerId"
          :style="panelStyle"
          @mousedown="onPanelMouseDown"
        >
          <li v-if="isSearchEnabled" class="ui-select__search" role="presentation">
            <Search :size="16" :stroke-width="2.2" aria-hidden="true" />

            <input
              ref="searchRef"
              v-model="query"
              type="text"
              class="ui-select__search-input"
              :placeholder="$t('Search')"
              :aria-label="$t('Search')"
              @keydown="onKeydown"
            />
          </li>

          <template v-if="showSelectAll && selectableVisible.length > 0">
            <li
              class="ui-select__option ui-select__all"
              role="option"
              :aria-selected="areAllVisibleSelected"
              @click="toggleAll"
            >
              <UiSelectCheck
                :checked="areAllVisibleSelected"
                :indeterminate="selectedOptions.length > 0"
              />

              <span class="ui-select__text">{{ $t("All") }}</span>

              <button
                v-if="selectedOptions.length > 0"
                type="button"
                class="ui-select__clear"
                @click.stop="clearAll"
              >
                {{ $t("Clear") }}
              </button>
            </li>

            <li class="ui-select__separator" role="presentation" />
          </template>

          <template v-for="(option, index) in visibleOptions" :key="String(option.value)">
            <li v-if="option.isGroupHeader" class="ui-select__group" role="presentation">
              {{ option.text }}
            </li>

            <li
              v-else
              :id="optionId(index)"
              class="ui-select__option"
              :class="{
                'is-selected': isSelected(option.value),
                'is-active': index === activeIndex,
                'is-disabled': option.disabled,
              }"
              role="option"
              :aria-selected="isSelected(option.value)"
              :aria-disabled="option.disabled || undefined"
              @click="toggle(option)"
              @mousemove="onOptionHover(option, index)"
            >
              <UiSelectCheck :checked="isSelected(option.value)" />

              <span class="ui-select__text">{{ option.text }}</span>
            </li>
          </template>

          <li v-if="visibleOptions.length === 0" class="ui-select__empty" role="presentation">
            {{ $t("No options found") }}
          </li>
        </ul>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useAttrs, useId, watch } from "vue";
import type { StyleValue } from "vue";
import { useI18n } from "vue-i18n";
import { ChevronDown, Search } from "lucide-vue-next";
import UiSelectCheck from "@/components/newUi/forms/UiSelectCheck.vue";
import { useAnchoredPopover } from "@/components/newUi/core/useAnchoredPopover";
import {
  isSelectableOption,
  useSelectList,
  type SelectListOption,
} from "@/components/newUi/forms/useSelectList";

type OptionValue = string | number | null;

const MAX_NAMES = 3;

const props = withDefaults(
  defineProps<{
    modelValue?: OptionValue[];
    options: SelectListOption[];
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    showSelectAll?: boolean;
  }>(),
  {
    modelValue: () => [],
    placeholder: "",
    disabled: false,
    searchable: false,
    showSelectAll: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: OptionValue[]];
}>();

defineOptions({ inheritAttrs: false });

const { t } = useI18n();
const attrs = useAttrs();
const baseId = useId();
const listboxId = `ui-multi-select-list-${baseId}`;
const triggerId = computed(() => (attrs.id as string | undefined) ?? `ui-multi-select-${baseId}`);
const buttonAttrs = computed(() =>
  Object.fromEntries(Object.entries(attrs).filter(([key]) => key !== "class" && key !== "style")),
);
const {
  rootRef,
  triggerRef,
  panelRef,
  isOpen,
  panelStyle,
  open: openPopover,
  close,
} = useAnchoredPopover({ matchWidth: true });
const searchRef = ref<HTMLInputElement | null>(null);
const valueRef = ref<HTMLElement | null>(null);
const valueWidth = ref(0);
const activeIndex = ref(0);

const { query, isSearchEnabled, visibleOptions, firstSelectableIndex, stepSelectableIndex } =
  useSelectList(
    () => props.options,
    () => props.searchable,
  );

const selectableOptions = computed(() => props.options.filter(isSelectableOption));

const selectableVisible = computed(() => visibleOptions.value.filter(isSelectableOption));

const selectedOptions = computed(() =>
  props.options.filter(
    (option) => !option.isGroupHeader && props.modelValue.includes(option.value),
  ),
);

const areAllVisibleSelected = computed(
  () =>
    selectableVisible.value.length > 0 &&
    selectableVisible.value.every((option) => props.modelValue.includes(option.value)),
);

const namesSummary = (shownCount: number): string => {
  const names = selectedOptions.value.slice(0, shownCount).map((option) => option.text);
  const hiddenCount = selectedOptions.value.length - shownCount;

  return hiddenCount > 0 ? `${names.join(", ")} +${hiddenCount}` : names.join(", ");
};

const measureText = (text: string): number | null => {
  if (!valueRef.value) {
    return null;
  }

  const context = document.createElement("canvas").getContext?.("2d");

  if (!context) {
    return null;
  }

  context.font = getComputedStyle(valueRef.value).font;

  return context.measureText(text).width;
};

const fittingNamesSummary = (): string => {
  const availableWidth = valueWidth.value;

  for (let shownCount = selectedOptions.value.length; shownCount > 1; shownCount--) {
    const candidate = namesSummary(shownCount);
    const width = measureText(candidate);

    if (width === null || availableWidth === 0 || width <= availableWidth) {
      return candidate;
    }
  }

  return namesSummary(1);
};

const summary = computed(() => {
  if (selectedOptions.value.length === 0) {
    return props.placeholder;
  }

  if (
    props.showSelectAll &&
    selectableOptions.value.length > MAX_NAMES &&
    selectableOptions.value.every((option) => props.modelValue.includes(option.value))
  ) {
    return t("All");
  }

  if (selectedOptions.value.length <= MAX_NAMES) {
    return fittingNamesSummary();
  }

  return t("Selected: {count}", { count: selectedOptions.value.length });
});

let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  if (!valueRef.value || typeof ResizeObserver === "undefined") {
    return;
  }

  resizeObserver = new ResizeObserver(([entry]) => {
    valueWidth.value = entry.contentRect.width;
  });

  resizeObserver.observe(valueRef.value);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});

const optionId = (index: number): string => `${listboxId}-option-${index}`;

const isSelected = (value: OptionValue): boolean => props.modelValue.includes(value);

const scrollActiveIntoView = (): void => {
  panelRef.value?.querySelector(".is-active")?.scrollIntoView?.({ block: "nearest" });
};

const open = async (): Promise<void> => {
  if (props.disabled || isOpen.value) {
    return;
  }

  query.value = "";
  activeIndex.value = firstSelectableIndex();

  openPopover();

  await nextTick();

  searchRef.value?.focus();
};

const toggle = (option: SelectListOption): void => {
  if (!isSelectableOption(option)) {
    return;
  }

  emit(
    "update:modelValue",
    isSelected(option.value)
      ? props.modelValue.filter((value) => value !== option.value)
      : [...props.modelValue, option.value],
  );
};

const clearAll = (): void => {
  emit("update:modelValue", []);
};

const toggleAll = (): void => {
  const visibleValues = selectableVisible.value.map((option) => option.value);

  if (areAllVisibleSelected.value) {
    emit(
      "update:modelValue",
      props.modelValue.filter((value) => !visibleValues.includes(value)),
    );

    return;
  }

  emit("update:modelValue", [
    ...props.modelValue,
    ...visibleValues.filter((value) => !props.modelValue.includes(value)),
  ]);
};

const onTriggerClick = (): void => {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
};

const onPanelMouseDown = (event: MouseEvent): void => {
  if (!(event.target as HTMLElement).closest(".ui-select__search-input")) {
    event.preventDefault();
  }
};

const onOptionHover = (option: SelectListOption, index: number): void => {
  if (isSelectableOption(option)) {
    activeIndex.value = index;
  }
};

const move = (delta: number): void => {
  activeIndex.value = stepSelectableIndex(activeIndex.value, delta);

  nextTick(scrollActiveIntoView);
};

const onKeydown = (event: KeyboardEvent): void => {
  const isSearchInput = (event.target as HTMLElement).classList.contains("ui-select__search-input");

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();

      if (isOpen.value) {
        move(1);
      } else {
        open();
      }
      break;
    case "ArrowUp":
      event.preventDefault();

      if (isOpen.value) {
        move(-1);
      } else {
        open();
      }
      break;
    case "Enter":
    case " ":
      if (event.key === " " && isSearchInput) {
        break;
      }

      event.preventDefault();

      if (isOpen.value) {
        const option = visibleOptions.value[activeIndex.value];

        if (option) {
          toggle(option);
        }
      } else {
        open();
      }
      break;
    case "Escape":
      if (isOpen.value) {
        event.preventDefault();
        event.stopPropagation();
        close();
        triggerRef.value?.focus();
      }
      break;
    case "Tab":
      close();
      break;
  }
};

watch(query, () => {
  activeIndex.value = firstSelectableIndex();
});

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) {
      close();
    }
  },
);
</script>
